import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult } from "@topzle/types";
import { z } from "zod";

const markSchema = z.enum(["X", "O"]);
const cellSchema = z.union([markSchema, z.null()]);
export const ticTacToeStateSchema = z.object({
  board: z.array(cellSchema).length(9),
  players: z.object({ X: z.string(), O: z.string() }),
  turn: markSchema,
  status: z.enum(["playing", "won", "draw"]),
  winner: markSchema.nullable(),
  finishReason: z.enum(["three_in_a_row", "board_full", "timeout"]).nullable(),
  winningLine: z.array(z.number().int().min(0).max(8)).length(3).nullable(),
  round: z.number().int().positive(),
  scores: z.record(z.string(), z.number().int().nonnegative()),
  initialClockSeconds: z.number().int().min(30).max(600).nullable(),
  clocks: z
    .object({ X: z.number().int().nonnegative(), O: z.number().int().nonnegative() })
    .nullable(),
  lastMoveAt: z.number().int().nonnegative(),
});
export const ticTacToeEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("place"), index: z.number().int().min(0).max(8) }),
  z.object({ type: z.literal("rematch") }),
]);
export const ticTacToeSettingsSchema = z.object({
  firstMark: markSchema.default("X"),
  clockSeconds: z.number().int().min(30).max(600).nullable().default(null),
});

export type TicTacToeState = z.infer<typeof ticTacToeStateSchema>;
export type TicTacToeEvent = z.infer<typeof ticTacToeEventSchema>;
export type TicTacToeSettings = z.infer<typeof ticTacToeSettingsSchema>;

const lines = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
] as const;

function outcome(
  board: TicTacToeState["board"],
): Pick<TicTacToeState, "status" | "winner" | "winningLine"> {
  for (const line of lines) {
    const [a, b, c] = line;
    const mark = board[a];
    if (mark && mark === board[b] && mark === board[c]) {
      return { status: "won", winner: mark, winningLine: [...line] };
    }
  }
  return board.every(Boolean)
    ? { status: "draw", winner: null, winningLine: null }
    : { status: "playing", winner: null, winningLine: null };
}

function minimax(
  board: TicTacToeState["board"],
  bot: "X" | "O",
  turn: "X" | "O",
  depth = 0,
): number {
  const result = outcome(board);
  if (result.status === "won") return result.winner === bot ? 10 - depth : depth - 10;
  if (result.status === "draw") return 0;
  const scores: number[] = [];
  for (let index = 0; index < board.length; index += 1) {
    if (board[index] !== null) continue;
    const next = [...board];
    next[index] = turn;
    scores.push(minimax(next, bot, turn === "X" ? "O" : "X", depth + 1));
  }
  return turn === bot ? Math.max(...scores) : Math.min(...scores);
}

export function chooseBotMove(
  state: TicTacToeState,
  bot: "X" | "O",
  difficulty: BotContext["difficulty"],
): number {
  const available = state.board.flatMap((cell, index) => (cell === null ? [index] : []));
  if (available.length === 0) throw new Error("No legal moves");
  if (difficulty === "easy") {
    const played = state.board.length - available.length;
    return available[(played * 5 + 1) % available.length] ?? available[0] ?? 0;
  }
  const scored = available.map((index) => {
    const next = [...state.board];
    next[index] = bot;
    return { index, score: minimax(next, bot, bot === "X" ? "O" : "X") };
  });
  const preference = (index: number) => (index === 4 ? 3 : [0, 2, 6, 8].includes(index) ? 2 : 1);
  scored.sort(
    (a, b) => b.score - a.score || preference(b.index) - preference(a.index) || a.index - b.index,
  );
  if (difficulty === "medium") {
    const immediateWin = scored.find(({ index }) => {
      const next = [...state.board];
      next[index] = bot;
      return outcome(next).winner === bot;
    });
    if (immediateWin) return immediateWin.index;
    const opponent = bot === "X" ? "O" : "X";
    const immediateBlock = available.find((index) => {
      const next = [...state.board];
      next[index] = opponent;
      return outcome(next).winner === opponent;
    });
    if (immediateBlock !== undefined) return immediateBlock;
    return scored.find((move) => move.score >= 0)?.index ?? scored[0]?.index ?? 0;
  }
  return scored[0]?.index ?? 0;
}

function timedOutState(state: TicTacToeState, now: number): TicTacToeState {
  if (!state.clocks || state.status !== "playing") return state;
  const remaining =
    state.clocks[state.turn] - Math.max(0, Math.floor((now - state.lastMoveAt) / 1000));
  if (remaining > 0) return state;
  const winner = state.turn === "X" ? "O" : "X";
  const winnerId = state.players[winner];
  return {
    ...state,
    status: "won",
    winner,
    finishReason: "timeout",
    winningLine: null,
    clocks: { ...state.clocks, [state.turn]: 0 },
    scores: { ...state.scores, [winnerId]: (state.scores[winnerId] ?? 0) + 1 },
  };
}

export const ticTacToeGame: TopzleGameDefinition<
  TicTacToeState,
  TicTacToeEvent,
  TicTacToeSettings
> = {
  id: "tic-tac-toe",
  version: "1.0.0",
  name: "Tic Tac Toe",
  shortDescription: "Fast rounds for two, with spectators and a perfect local bot.",
  longDescription:
    "Classic noughts and crosses with authoritative turns, rematches, history, and deterministic AI.",
  category: "board",
  icon: "grid-3x3",
  cover: "tic-tac-toe",
  minPlayers: 2,
  maxPlayers: 2,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "bubbles",
  stateSchema: ticTacToeStateSchema,
  eventSchema: ticTacToeEventSchema,
  settingsSchema: ticTacToeSettingsSchema,
  createInitialState(context, settings) {
    const [first = "player-x", second = "player-o"] = context.playerIds;
    return {
      board: Array(9).fill(null),
      players: { X: first, O: second },
      turn: settings.firstMark,
      status: "playing",
      winner: null,
      finishReason: null,
      winningLine: null,
      round: 1,
      scores: { [first]: 0, [second]: 0 },
      initialClockSeconds: settings.clockSeconds,
      clocks:
        settings.clockSeconds === null
          ? null
          : { X: settings.clockSeconds, O: settings.clockSeconds },
      lastMoveAt: context.now,
    };
  },
  validateEvent(state, event, context) {
    if (event.type === "rematch")
      return {
        valid: state.status !== "playing",
        code: "round_active",
        message: "Finish this round first",
      };
    if (state.status !== "playing")
      return { valid: false, code: "round_finished", message: "The round is over" };
    if (context.actorId !== state.players[state.turn])
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    if (state.board[event.index] !== null)
      return { valid: false, code: "occupied", message: "That square is occupied" };
    return { valid: true };
  },
  reduceEvent(state, event, context) {
    if (event.type === "rematch")
      return {
        ...state,
        board: Array(9).fill(null),
        turn: state.round % 2 === 1 ? "O" : "X",
        status: "playing",
        winner: null,
        finishReason: null,
        winningLine: null,
        round: state.round + 1,
        clocks:
          state.initialClockSeconds === null
            ? null
            : { X: state.initialClockSeconds, O: state.initialClockSeconds },
        lastMoveAt: context.now,
      };
    const afterClock = timedOutState(state, context.now);
    if (afterClock.status !== "playing") return afterClock;
    const board = [...state.board];
    board[event.index] = state.turn;
    const result = outcome(board);
    const scores = { ...state.scores };
    if (result.winner) {
      const winnerId = state.players[result.winner];
      scores[winnerId] = (scores[winnerId] ?? 0) + 1;
    }
    const clocks = state.clocks
      ? {
          ...state.clocks,
          [state.turn]: Math.max(
            0,
            state.clocks[state.turn] - Math.floor((context.now - state.lastMoveAt) / 1000),
          ),
        }
      : null;
    return {
      ...state,
      board,
      ...result,
      finishReason:
        result.status === "won" ? "three_in_a_row" : result.status === "draw" ? "board_full" : null,
      scores,
      clocks,
      lastMoveAt: context.now,
      turn: state.turn === "X" ? "O" : "X",
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions(state, playerId) {
    if (state.status !== "playing" || state.players[state.turn] !== playerId) return [];
    return state.board.flatMap((cell, index) => (cell === null ? [{ type: "place", index }] : []));
  },
  calculateResult(state): GameResult | null {
    if (state.status === "playing") return null;
    const winnerId = state.winner ? state.players[state.winner] : null;
    return {
      status: winnerId ? "completed" : "draw",
      winnerIds: winnerId ? [winnerId] : [],
      scores: state.scores,
      reason: state.finishReason ?? (winnerId ? "three_in_a_row" : "board_full"),
    };
  },
  async createBotAction(state, botContext) {
    const botMark = state.players.X === botContext.playerId ? "X" : "O";
    if (botContext.policy === "random") {
      const available = state.board.flatMap((cell, index) => (cell === null ? [index] : []));
      const index = available[Math.abs(botContext.now + state.board.length) % available.length];
      if (index === undefined) throw new Error("No legal moves");
      return { type: "place", index };
    }
    const difficulty = ["easy", "medium", "hard"].includes(botContext.policy ?? "")
      ? (botContext.policy as BotContext["difficulty"])
      : botContext.difficulty;
    return { type: "place", index: chooseBotMove(state, botMark, difficulty) };
  },
  getNextDeadline(state) {
    if (state.status !== "playing" || !state.clocks) return null;
    return state.lastMoveAt + state.clocks[state.turn] * 1000;
  },
  handleDeadline(state, context) {
    return timedOutState(state, context.now);
  },
  handlePlayerDeparture(state, departure, context) {
    const mark =
      state.players.X === departure.playerId
        ? "X"
        : state.players.O === departure.playerId
          ? "O"
          : null;
    if (!mark || !departure.replacementPlayerId) return null;
    const replacementPlayerId = departure.replacementPlayerId;
    const scores = { ...state.scores };
    scores[replacementPlayerId] = scores[departure.playerId] ?? 0;
    delete scores[departure.playerId];
    return {
      state: {
        ...state,
        players: { ...state.players, [mark]: replacementPlayerId },
        scores,
        lastMoveAt: context.now,
      },
      replacementPlayerId,
    };
  },
};
